<template>
  <div class="pendencies-report">
    <b-row class="h-100 no-gutters">
      <!-- Coluna da esquerda - Relatório -->
      <b-col cols="9" class="h-100">
        <b-card v-if="isMasterUser" id="report" no-body class="h-100">
          <div class="report-print-root" :class="printRootClass">
            <ReportPrintHeader
              v-if="printing"
              :title="printTitle"
              :subtitle="`Relatório de pendências internas · Total de usuários: ${totalUsersCount}`"
              :logo="printLogo"
              :generated-at="formatPrintDateTime(printGeneratedAt)"
              :company="printCompany"
              :period="printPeriod"
              :emitter="printEmitter"
            />
            <b-card-body class="position-relative">
              <div
                class="d-flex justify-content-between align-items-center mb-3 report-screen-heading"
              >
                <h2>{{ reportTitle }}</h2>
                <b-badge variant="info" pill class="p-2">
                  <i class="fas fa-file-alt mr-1"></i>
                  Total de Usuários: {{ totalUsersCount }}
                </b-badge>
              </div>

              <div class="complaints-layout-surface">
                <div v-if="loadingReport" class="skeleton-overlay">
                  <div class="table-responsive">
                    <table class="table complaints-layout-table skeleton-table">
                      <thead class="complaints-layout-header">
                        <tr>
                          <th
                            v-for="field in activeUserFields"
                            :key="field.key"
                          >
                            {{ field.label }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="i in 8" :key="i">
                          <td
                            v-for="field in activeUserFields"
                            :key="field.key"
                          >
                            <b-skeleton
                              :width="field.key === 'count' ? '70px' : '90%'"
                            ></b-skeleton>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div
                  v-if="!loadingReport && reportItems.length === 0"
                  class="complaints-layout-empty text-center"
                >
                  <p>Nenhuma pendência interna encontrada.</p>
                </div>

                <b-table
                  v-show="!loadingReport && reportItems.length > 0"
                  borderless
                  hover
                  :responsive="!printing"
                  :items="reportItems"
                  :fields="printFields"
                  :tbody-tr-class="getUserRowClass"
                  table-class="complaints-layout-table internal-pendency-summary-table"
                  thead-class="complaints-layout-header"
                >
                  <template #cell(name)="data">
                    <div
                      class="internal-pendency-main-cell d-flex align-items-center"
                    >
                      <b-avatar
                        size="sm"
                        variant="light-primary"
                        class="mr-2"
                      ></b-avatar>
                      <div>
                        <strong class="internal-pendency-main-cell__title">{{
                          data.value
                        }}</strong>
                        <small
                          class="internal-pendency-main-cell__subtitle d-block"
                        >
                          {{ data.item.count }} protocolo{{
                            data.item.count !== 1 ? "s" : ""
                          }}
                          vinculado{{ data.item.count !== 1 ? "s" : "" }}
                        </small>
                      </div>
                    </div>
                  </template>

                  <template #cell(companyName)="data">
                    <span class="internal-pendency-company-name">{{
                      data.value
                    }}</span>
                  </template>

                  <template #cell(status_summary)="data">
                    <div
                      class="summary-badges d-flex align-items-center justify-content-center flex-wrap"
                    >
                      <span class="status-badge badge-soft-warning mr-1 mb-1">
                        {{ data.item.openCount }} aberta{{
                          data.item.openCount !== 1 ? "s" : ""
                        }}
                      </span>
                      <span class="status-badge badge-soft-success mb-1">
                        {{ data.item.resolvedCount }} resolvida{{
                          data.item.resolvedCount !== 1 ? "s" : ""
                        }}
                      </span>
                    </div>
                  </template>

                  <template #cell(count)="data">
                    <span class="tasks-count-pill">{{ data.value }}</span>
                  </template>

                  <template #cell(details)="data">
                    <div class="d-flex justify-content-end">
                      <b-button
                        size="sm"
                        variant="outline-primary"
                        class="toggle-details-btn"
                        @click.stop="toggleRowDetails(data.item)"
                      >
                        <i
                          :class="
                            data.item._showDetails
                              ? 'fas fa-chevron-up'
                              : 'fas fa-chevron-down'
                          "
                          class="mr-1"
                        ></i>
                        {{
                          data.item._showDetails ? "Ocultar" : "Ver pendências"
                        }}
                      </b-button>
                    </div>
                  </template>

                  <template #row-details="data">
                    <div class="internal-pendency-details-panel">
                      <div class="internal-pendency-details-panel__header">
                        <div>
                          <p class="internal-pendency-details-panel__eyebrow">
                            Pendências internas
                          </p>
                          <h6
                            class="internal-pendency-details-panel__title mb-0"
                          >
                            {{ data.item.name }}
                          </h6>
                        </div>

                        <span class="tasks-count-pill tasks-count-pill--subtle">
                          {{ data.item.pendencies.length }} pendência{{
                            data.item.pendencies.length !== 1 ? "s" : ""
                          }}
                        </span>
                      </div>

                      <div
                        v-if="
                          data.item.pendencies && data.item.pendencies.length
                        "
                        class="internal-pendency-task-list"
                      >
                        <article
                          v-for="pendency in data.item.pendencies"
                          :key="pendency._id"
                          class="pendency-detail-card"
                        >
                          <div class="pendency-detail-card__main">
                            <div class="pendency-detail-card__label">
                              Protocolo
                            </div>
                            <div class="pendency-detail-card__protocol mb-2">
                              <b-button
                                v-if="!printing"
                                size="sm"
                                variant="link"
                                class="p-0 complaints-layout-link"
                                :to="`/denuncias/${pendency.complaintId}?event=${pendency._id}`"
                                title="Ver Denúncia"
                              >
                                {{ pendency.protocol }}
                                <i
                                  class="fas fa-external-link-alt ml-1"
                                  aria-hidden="true"
                                  style="font-size: 0.8rem"
                                />
                              </b-button>
                              <span
                                v-else
                                class="text-primary font-weight-bold"
                              >
                                {{ pendency.protocol }}
                              </span>
                            </div>

                            <div class="pendency-detail-card__label">
                              Atividade / Descrição
                            </div>
                            <p class="pendency-detail-card__description mb-0">
                              {{ pendency.description || "-" }}
                            </p>
                          </div>

                          <div class="pendency-detail-card__meta">
                            <div class="pendency-detail-card__meta-item">
                              <span class="pendency-detail-card__label"
                                >Criação</span
                              >
                              <strong>{{
                                formatDate(pendency.createdAt)
                              }}</strong>
                            </div>

                            <div class="pendency-detail-card__meta-item">
                              <span class="pendency-detail-card__label"
                                >Prazo limite</span
                              >
                              <strong
                                :class="{
                                  'text-danger': isOverdue(pendency),
                                }"
                              >
                                {{ formatDate(pendency.dueDate) }}
                              </strong>
                              <small
                                v-if="isOverdue(pendency)"
                                class="pendency-detail-card__late-note"
                              >
                                <i class="fas fa-clock mr-1"></i>Atrasada
                              </small>
                            </div>
                          </div>

                          <div class="pendency-detail-card__status">
                            <span
                              :class="getPendencyStatusClass(pendency.status)"
                              class="status-badge"
                            >
                              {{ pendency.status }}
                            </span>
                          </div>
                        </article>
                      </div>

                      <div
                        v-else
                        class="internal-pendency-details-panel__empty"
                      >
                        Nenhuma pendência encontrada para este usuário.
                      </div>
                    </div>
                  </template>
                </b-table>
              </div>
            </b-card-body>
          </div>
        </b-card>

        <b-card v-else id="report" class="h-100">
          <div class="report-print-root" :class="printRootClass">
            <ReportPrintHeader
              v-if="printing"
              :title="printTitle"
              :subtitle="`Relatório de pendências internas · Total de usuários: ${totalUsersCount}`"
              :logo="printLogo"
              :generated-at="formatPrintDateTime(printGeneratedAt)"
              :company="printCompany"
              :period="printPeriod"
              :emitter="printEmitter"
            />
            <div
              class="d-flex justify-content-between align-items-center mb-3 report-screen-heading"
            >
              <h2>{{ reportTitle }}</h2>
              <b-badge variant="info" pill class="p-2">
                <i class="fas fa-file-alt mr-1"></i>
                Total de Usuários: {{ totalUsersCount }}
              </b-badge>
            </div>
            <b-card-body class="position-relative">
              <div class="complaints-layout-surface">
                <div v-if="loadingReport" class="skeleton-overlay">
                  <div class="table-responsive">
                    <table class="table complaints-layout-table skeleton-table">
                      <thead class="complaints-layout-header">
                        <tr>
                          <th
                            v-for="field in activeUserFields"
                            :key="field.key"
                          >
                            {{ field.label }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="i in 8" :key="i">
                          <td
                            v-for="field in activeUserFields"
                            :key="field.key"
                          >
                            <b-skeleton
                              :width="field.key === 'count' ? '70px' : '90%'"
                            ></b-skeleton>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div
                  v-if="!loadingReport && reportItems.length === 0"
                  class="complaints-layout-empty text-center"
                >
                  <p>Nenhuma pendência interna encontrada.</p>
                </div>

                <b-table
                  v-show="!loadingReport && reportItems.length > 0"
                  borderless
                  hover
                  :responsive="!printing"
                  :items="reportItems"
                  :fields="printFields"
                  :tbody-tr-class="getUserRowClass"
                  table-class="complaints-layout-table internal-pendency-summary-table"
                  thead-class="complaints-layout-header"
                >
                  <template #cell(name)="data">
                    <div
                      class="internal-pendency-main-cell d-flex align-items-center"
                    >
                      <b-avatar
                        size="sm"
                        variant="light-primary"
                        class="mr-2"
                      ></b-avatar>
                      <div>
                        <strong class="internal-pendency-main-cell__title">{{
                          data.value
                        }}</strong>
                        <small
                          class="internal-pendency-main-cell__subtitle d-block"
                        >
                          {{ data.item.count }} protocolo{{
                            data.item.count !== 1 ? "s" : ""
                          }}
                          vinculado{{ data.item.count !== 1 ? "s" : "" }}
                        </small>
                      </div>
                    </div>
                  </template>

                  <template #cell(companyName)="data">
                    <span class="internal-pendency-company-name">{{
                      data.value
                    }}</span>
                  </template>

                  <template #cell(status_summary)="data">
                    <div
                      class="summary-badges d-flex align-items-center justify-content-center flex-wrap"
                    >
                      <span class="status-badge badge-soft-warning mr-1 mb-1">
                        {{ data.item.openCount }} aberta{{
                          data.item.openCount !== 1 ? "s" : ""
                        }}
                      </span>
                      <span class="status-badge badge-soft-success mb-1">
                        {{ data.item.resolvedCount }} resolvida{{
                          data.item.resolvedCount !== 1 ? "s" : ""
                        }}
                      </span>
                    </div>
                  </template>

                  <template #cell(count)="data">
                    <span class="tasks-count-pill">{{ data.value }}</span>
                  </template>

                  <template #cell(details)="data">
                    <div class="d-flex justify-content-end">
                      <b-button
                        size="sm"
                        variant="outline-primary"
                        class="toggle-details-btn"
                        @click.stop="toggleRowDetails(data.item)"
                      >
                        <i
                          :class="
                            data.item._showDetails
                              ? 'fas fa-chevron-up'
                              : 'fas fa-chevron-down'
                          "
                          class="mr-1"
                        ></i>
                        {{
                          data.item._showDetails ? "Ocultar" : "Ver pendências"
                        }}
                      </b-button>
                    </div>
                  </template>

                  <template #row-details="data">
                    <div class="internal-pendency-details-panel">
                      <div class="internal-pendency-details-panel__header">
                        <div>
                          <p class="internal-pendency-details-panel__eyebrow">
                            Pendências internas
                          </p>
                          <h6
                            class="internal-pendency-details-panel__title mb-0"
                          >
                            {{ data.item.name }}
                          </h6>
                        </div>

                        <span class="tasks-count-pill tasks-count-pill--subtle">
                          {{ data.item.pendencies.length }} pendência{{
                            data.item.pendencies.length !== 1 ? "s" : ""
                          }}
                        </span>
                      </div>

                      <div
                        v-if="
                          data.item.pendencies && data.item.pendencies.length
                        "
                        class="internal-pendency-task-list"
                      >
                        <article
                          v-for="pendency in data.item.pendencies"
                          :key="pendency._id"
                          class="pendency-detail-card"
                        >
                          <div class="pendency-detail-card__main">
                            <div class="pendency-detail-card__label">
                              Protocolo
                            </div>
                            <div class="pendency-detail-card__protocol mb-2">
                              <b-button
                                v-if="!printing"
                                size="sm"
                                variant="link"
                                class="p-0 complaints-layout-link"
                                :to="`/denuncias/${pendency.complaintId}?event=${pendency._id}`"
                                title="Ver Denúncia"
                              >
                                {{ pendency.protocol }}
                                <i
                                  class="fas fa-external-link-alt ml-1"
                                  aria-hidden="true"
                                  style="font-size: 0.8rem"
                                />
                              </b-button>
                              <span
                                v-else
                                class="text-primary font-weight-bold"
                              >
                                {{ pendency.protocol }}
                              </span>
                            </div>

                            <div class="pendency-detail-card__label">
                              Atividade / Descrição
                            </div>
                            <p class="pendency-detail-card__description mb-0">
                              {{ pendency.description || "-" }}
                            </p>
                          </div>

                          <div class="pendency-detail-card__meta">
                            <div class="pendency-detail-card__meta-item">
                              <span class="pendency-detail-card__label"
                                >Criação</span
                              >
                              <strong>{{
                                formatDate(pendency.createdAt)
                              }}</strong>
                            </div>

                            <div class="pendency-detail-card__meta-item">
                              <span class="pendency-detail-card__label"
                                >Prazo limite</span
                              >
                              <strong
                                :class="{
                                  'text-danger': isOverdue(pendency),
                                }"
                              >
                                {{ formatDate(pendency.dueDate) }}
                              </strong>
                              <small
                                v-if="isOverdue(pendency)"
                                class="pendency-detail-card__late-note"
                              >
                                <i class="fas fa-clock mr-1"></i>Atrasada
                              </small>
                            </div>
                          </div>

                          <div class="pendency-detail-card__status">
                            <span
                              :class="getPendencyStatusClass(pendency.status)"
                              class="status-badge"
                            >
                              {{ pendency.status }}
                            </span>
                          </div>
                        </article>
                      </div>

                      <div
                        v-else
                        class="internal-pendency-details-panel__empty"
                      >
                        Nenhuma pendência encontrada para este usuário.
                      </div>
                    </div>
                  </template>
                </b-table>
              </div>
            </b-card-body>
          </div>
        </b-card>
      </b-col>

      <!-- Coluna da direita - Filtros -->
      <b-col cols="3" class="pl-3">
        <b-card header="Parâmetros" class="filters-sidebar h-100">
          <div class="filters-container">
            <!-- Filtro de Empresa -->
            <div v-if="showCompanyFilter" class="filter-group">
              <div class="filter-header" @click="toggleSection('empresa')">
                <h6 class="filter-title">Empresa</h6>
                <b-button variant="link" size="sm" class="collapse-btn p-0">
                  <i
                    :class="
                      collapsedSections.empresa
                        ? 'fas fa-chevron-down'
                        : 'fas fa-chevron-up'
                    "
                  />
                </b-button>
              </div>
              <b-collapse
                id="collapse-empresa"
                :visible="!collapsedSections.empresa"
              >
                <b-form-group label="Selecionar Empresa" class="mb-3">
                  <b-form-select
                    v-model="filter.selectedCompany"
                    :options="companyOptions"
                    class="mb-2"
                    @change="onFilterChange"
                  >
                    <template #first>
                      <b-form-select-option :value="null"
                        >Todas as Empresas</b-form-select-option
                      >
                    </template>
                  </b-form-select>
                </b-form-group>
              </b-collapse>
            </div>

            <div class="d-flex flex-column mt-3">
              <b-button variant="primary" @click="print" class="mb-2 shadow-sm">
                <i class="fas fa-print mr-2"></i> Imprimir Relatório
              </b-button>
              <b-button
                variant="outline-primary"
                @click="loadReport"
                icon="fas fa-sync-alt"
                class="shadow-sm"
              >
                <i class="fas fa-sync-alt mr-2"></i> Atualizar Dados
              </b-button>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { pendenciesReport } from "@/api/reports";
import moment from "moment";
import { getCompanies } from "@/api/company";
import ReportPrintHeader from "@/components/ReportPrintHeader.vue";
import reportPrintMixin from "@/mixins/reportPrint";

export default {
  name: "PendenciesReport",
  components: {
    ReportPrintHeader,
  },
  mixins: [reportPrintMixin],
  data() {
    return {
      printAllDetails: false,
      loadingReport: false,
      filter: {
        selectedCompany: null,
      },
      currentFilter: {
        selectedCompany: null,
      },
      report: [],
      availableCompanies: [],
      collapsedSections: {
        empresa: false,
      },
      userFields: [
        { key: "name", label: "Usuário Responsável", sortable: true },
        {
          key: "status_summary",
          label: "Status",
          tdClass: "text-center",
          thClass: "text-center",
        },
        {
          key: "count",
          label: "Total de Protocolos",
          sortable: true,
          tdClass: "text-center",
          thClass: "text-center",
        },
        {
          key: "details",
          label: "",
          tdClass: "text-right",
          thClass: "text-right internal-pendency-details-header",
        },
      ],
    };
  },
  computed: {
    ...mapGetters(["profile"]),
    isMasterUser() {
      return this.profile.role === "root" || this.isCompanyManager;
    },
    isCompanyManager() {
      return (
        this.profile.role === "company-root" &&
        this.profile.managedCompanies &&
        this.profile.managedCompanies.length > 0
      );
    },
    showCompanyFilter() {
      return this.profile.role === "root" || this.isCompanyManager;
    },
    companyOptions() {
      return this.availableCompanies.map((company) => ({
        value: company._id,
        text: company.fantasyName || company.name,
      }));
    },
    showCompanyColumn() {
      return this.isMasterUser && !this.currentFilter.selectedCompany;
    },
    activeUserFields() {
      if (!this.showCompanyColumn) {
        return this.userFields;
      }

      return [
        { key: "companyName", label: "Empresa", sortable: true },
        ...this.userFields,
      ];
    },
    filteredReport() {
      if (!this.showCompanyFilter || !this.currentFilter.selectedCompany) {
        return this.report;
      }
      if (!Array.isArray(this.report)) return [];
      return this.report.filter(
        (item) =>
          item.company &&
          item.company._id === this.currentFilter.selectedCompany
      );
    },
    reportItems() {
      if (this.isMasterUser) {
        const groupedReport = Array.isArray(this.filteredReport)
          ? this.filteredReport
          : [];

        return groupedReport.flatMap((item) => {
          const companyName =
            item.company?.fantasyName || item.company?.name || "N/A";

          return (item.users || []).map((user) => ({
            ...user,
            companyName,
            _showDetails: this.printAllDetails || user._showDetails || false,
          }));
        });
      }

      return (this.report.users || []).map((user) => ({
        ...user,
        companyName:
          this.report.company?.fantasyName || this.report.company?.name || "",
        _showDetails: this.printAllDetails || user._showDetails || false,
      }));
    },
    reportTitle() {
      if (!this.isMasterUser) {
        return `Pendência Interna - ${
          this.report.company ? this.report.company.fantasyName : "Empresa"
        }`;
      }

      if (this.currentFilter.selectedCompany) {
        const selectedCompany = this.companyOptions.find(
          (company) => company.value === this.currentFilter.selectedCompany
        );

        return `Pendência Interna - ${
          selectedCompany ? selectedCompany.text : "Empresa Selecionada"
        }`;
      }

      return "Pendência Interna";
    },
    printTitle() {
      return this.reportTitle;
    },
    totalUsersCount() {
      return this.reportItems.length;
    },
  },
  async created() {
    await this.loadCompanyPrintLogo(this.profile.company);
    if (this.showCompanyFilter) {
      try {
        const response = await getCompanies();
        this.availableCompanies = response.data;
      } catch (e) {
        console.error("Erro ao carregar empresas");
      }
    }
    await this.loadReport();
  },
  methods: {
    async loadReport() {
      this.loadingReport = true;

      try {
        const companyId = this.isMasterUser ? null : this.profile.company;
        const response = await pendenciesReport(companyId);
        this.report = response.data;
      } catch (error) {
        console.error("Erro ao carregar relatório:", error);
      } finally {
        this.loadingReport = false;
      }
    },
    onFilterChange() {
      this.currentFilter.selectedCompany = this.filter.selectedCompany;
    },
    toggleSection(section) {
      this.$set(
        this.collapsedSections,
        section,
        !this.collapsedSections[section]
      );
    },
    toggleRowDetails(item) {
      this.$set(item, "_showDetails", !item._showDetails);
    },
    getUserRowClass(item, type) {
      if (type !== "row" || !item) {
        return "";
      }

      return item._showDetails
        ? "complaints-layout-row complaints-layout-row--expanded"
        : "complaints-layout-row";
    },
    getPendencyStatusClass(status) {
      return status === "resolvida"
        ? "badge-soft-success"
        : "badge-soft-warning";
    },
    formatDate(value) {
      if (!value) return "—";
      return moment(value).format("DD/MM/YYYY");
    },
    isOverdue(pendency) {
      if (!pendency || !pendency.dueDate || pendency.status === "resolvida") {
        return false;
      }

      return moment(pendency.dueDate).isBefore(moment(), "day");
    },
    async print() {
      this.printAllDetails = true;
      await this.$nextTick();

      await this.printReport({
        onComplete: () => {
          this.printAllDetails = false;
        },
      });
    },
  },
};
</script>

<style scoped>
.pendencies-report {
  height: 100%;
}

.filters-sidebar {
  border-left: 1px solid #dee2e6;
  background-color: #f8f9fa;
}

.filter-group {
  margin-bottom: 1.5rem;
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 1rem;
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  margin-bottom: 0.5rem;
}

.filter-title {
  margin-bottom: 0;
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
  text-transform: uppercase;
}

.skeleton-overlay {
  position: relative;
}

.skeleton-table {
  border-spacing: 0;
}

.skeleton-table tbody tr {
  box-shadow: none;
}

.skeleton-table tbody td {
  background-color: #fff;
}

.internal-pendency-main-cell__title {
  color: #1f2d3d;
  font-size: 0.95rem;
}

.internal-pendency-main-cell__subtitle {
  color: #7a8794;
  font-size: 0.75rem;
  margin-top: 0.15rem;
}

.internal-pendency-company-name {
  color: #495057;
  font-weight: 500;
}

.status-badge {
  padding: 0.38rem 0.8rem;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.badge-soft-warning {
  background-color: #fef3c7;
  color: #92400e;
}

.badge-soft-success {
  background-color: #dcfce7;
  color: #166534;
}

.badge-soft-info {
  background-color: #e8f1ff;
  color: #2958a5;
}

.tasks-count-pill {
  display: inline-flex;
  min-width: 2.25rem;
  justify-content: center;
  align-items: center;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  background: #eef4ff;
  color: #2f5fb8;
  font-weight: 700;
}

.tasks-count-pill--subtle {
  background: #f3f7ff;
  color: #3c6fd1;
}

.toggle-details-btn {
  border-radius: 999px;
  padding: 0.35rem 0.9rem;
  font-weight: 600;
  white-space: nowrap;
}

.internal-pendency-details-panel {
  margin: 0.75rem 1rem 1rem;
  padding: 1rem;
  border: 1px solid #d9e7ff;
  border-radius: 12px;
  background: linear-gradient(180deg, #fbfdff 0%, #f5f9ff 100%);
}

.internal-pendency-details-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.9rem;
}

.internal-pendency-details-panel__eyebrow {
  margin-bottom: 0.25rem;
  color: #6f7d8b;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.internal-pendency-details-panel__title {
  color: #183153;
  font-weight: 700;
}

.internal-pendency-task-list {
  display: grid;
  gap: 0.75rem;
}

.pendency-detail-card {
  display: grid;
  grid-template-columns: minmax(0, 2.2fr) minmax(220px, 1.3fr) auto;
  gap: 1rem;
  align-items: start;
  padding: 1rem;
  border-radius: 10px;
  border: 1px solid #e3ebf7;
  background: #fff;
  box-shadow: 0 1px 2px rgba(17, 43, 85, 0.05);
}

.pendency-detail-card__label {
  display: block;
  margin-bottom: 0.25rem;
  color: #7a8794;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.pendency-detail-card__description {
  color: #243447;
  line-height: 1.45;
}

.pendency-detail-card__meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.pendency-detail-card__meta-item {
  display: flex;
  flex-direction: column;
}

.pendency-detail-card__late-note {
  margin-top: 0.3rem;
  color: #dc3545;
  font-weight: 600;
}

.pendency-detail-card__status {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
}

.internal-pendency-details-panel__empty {
  padding: 0.5rem 0;
  color: #6c757d;
}

::v-deep .internal-pendency-details-header {
  width: 1%;
  white-space: nowrap;
}

::v-deep
  .internal-pendency-summary-table
  tbody
  tr:hover:not(.b-table-details)
  td {
  background-color: #fafcff;
}

::v-deep .complaints-layout-row.complaints-layout-row--expanded td {
  background-color: #f8fbff;
}

::v-deep .b-table-details > td {
  cursor: default;
  padding: 0;
  background-color: #f8fbff;
}

@media (max-width: 991.98px) {
  .internal-pendency-details-panel__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .pendency-detail-card {
    grid-template-columns: 1fr;
  }

  .pendency-detail-card__meta {
    grid-template-columns: 1fr 1fr;
  }

  .pendency-detail-card__status {
    justify-content: flex-start;
  }
}

@media (max-width: 575.98px) {
  .internal-pendency-details-panel {
    margin: 0.5rem;
    padding: 0.85rem;
  }

  .pendency-detail-card {
    padding: 0.85rem;
  }

  .pendency-detail-card__meta {
    grid-template-columns: 1fr;
  }

  .toggle-details-btn {
    padding-left: 0.7rem;
    padding-right: 0.7rem;
  }
}
</style>
